import { TYPES } from '../../../render/constants';
import { getNodeMatrix } from '../../../render/effects/matrix';
import { getPaddingBox } from '../../../render/text';
import { serializePadding } from '../../../common-utils';
import { resolveTextStrokeProps, resolveTextStrokes } from '../../../render/utils/text-stroke';
import { isValidNumber, safeParse } from '../../../common-utils/math';
// --- HELPER FUNCTIONS ---

/**
 * Converts a camelCase string to kebab-case for CSS property names.
 * @param {string} str The string to convert.
 * @returns {string} The kebab-cased string.
 */
function kebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Extracts a solid color from a fill string (which could be a gradient).
 * @param {string} fill The fill string.
 * @returns {string|null} The extracted solid color or null.
 */
function getSolidColor(fill) {
  if (!fill || typeof fill !== 'string' || !fill.includes('gradient')) {
    return fill;
  }
  // Look for hex or rgb(a) color values as a fallback
  const colorStop = fill.match(/#([0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3})|rgba?\([^)]+\)/);
  return colorStop ? colorStop[0] : null;
}

/**
 * Generates a border-radius CSS value from a node's cornerRadius property.
 * @param {object} node The node object.
 * @returns {string} The CSS border-radius value or an empty string.
 */
function getBorderRadius(node) {
  if (!node.cornerRadius) return '';
  if (typeof node.cornerRadius === 'object') {
    // Handle individual corner radii
    return `${node.cornerRadius.topLeft || 0}px ${node.cornerRadius.topRight || 0}px ${node.cornerRadius.bottomRight || 0}px ${node.cornerRadius.bottomLeft || 0}px`;
  }
  // Handle uniform radius
  return `${node.cornerRadius}px`;
}

// --- CORE RENDERING LOGIC ---

/**
 * Creates an HTML element for a given node, optimized for email client compatibility.
 * This function styles the parent `<td>` (e.g., for background colors) and may insert
 * child elements like `<img>` or `<div>` for content.
 * @param {object} node - The node object to render.
 * @param {HTMLTableCellElement} parentTd - The `<td>` element that will contain the rendered node.
 * @param {object} store - The data store with API access.
 * @param {HTMLDocument} document - The document to create elements in.
 * @param {{w: number, h: number}} dimensions - The dimensions the element should occupy.
 */
function createEmailCompatibleElement(node, parentTd, store, document, { w, h }) {
  let element;
  const opacity = node.opacity ?? 1;

  switch (node.type) {
    case TYPES.text: {
      element = document.createElement('div');
      const padding = getPaddingBox(node);
      const solidFill = getSolidColor(node.fill);

      // Handle text background color on parent TD for email compatibility
      if (node.background) {
        const solidBg = getSolidColor(node.background);
        if (solidBg && solidBg !== 'transparent') {
          parentTd.setAttribute('bgcolor', solidBg);
          parentTd.style.backgroundColor = solidBg;
        }
      }

      const textStyles = {
        width: `${w - (padding.left + padding.right)}px`,
        margin: '0',
        padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
        fontFamily: node.fontFamily || 'Arial, sans-serif',
        fontSize: `${node.fontSize || 14}px`,
        lineHeight: node.lineHeight || '1.2',
        textAlign: node.textAlign || 'left',
        color: solidFill || '#000000',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        boxSizing: 'border-box',
      };
      if (node.fontWeight) textStyles.fontWeight = node.fontWeight;
      if (node.fontStyle) textStyles.fontStyle = node.fontStyle;
      if (node.decoration) textStyles.textDecoration = node.decoration;
      if (node.letterSpacing) textStyles.letterSpacing = `${node.letterSpacing}px`;

      // Handle gradient text for modern clients
      if (node.fill?.includes('gradient')) {
        textStyles.background = node.fill;
        textStyles.webkitBackgroundClip = 'text';
        textStyles.backgroundClip = 'text';
        textStyles.color = 'transparent';
      }

      // Handle background on the div element for modern clients
      if (node.background && node.background.includes('gradient')) {
        textStyles.background = node.background;
      } else if (node.background && !node.background.includes('gradient')) {
        textStyles.backgroundColor = node.background;
      }

      const borderRadius = getBorderRadius(node);
      if (borderRadius) {
        textStyles.borderRadius = borderRadius;
      }

      element.style.cssText = Object.entries(textStyles)
        .map(([k, v]) => `${kebabCase(k)}: ${v}`)
        .join('; ');
      element.innerHTML = (node.text || '').replace(/\n/g, '<br />');
      break;
    }

    case TYPES.image:
      {
        element = document.createElement('img');
        element.setAttribute('alt', node.prompt || 'image');
        element.setAttribute('width', Math.round(w));
        element.setAttribute('height', Math.round(h));
        if (node.src) {
          if (node.src.startsWith('asset://')) {
            element.src = `https://assets.seede.ai/asset/${node.src.replace('asset://', '')}`;
          } else {
            element.src = node.src;
          }
        }

        let imgCss = `display: block; width: ${Math.round(w)}px; height: ${Math.round(h)}px; border: 0; box-sizing: border-box;`;

        // Border on TD is a fallback. For modern clients, apply to image.
        if (node.stroke && node.strokeWidth) {
          parentTd.style.border = `${node.strokeWidth}px solid ${node.stroke}`;
          imgCss += ` border: ${node.strokeWidth}px solid ${node.stroke};`;
        }

        const imgBorderRadius = getBorderRadius(node);
        if (imgBorderRadius) {
          imgCss += ` border-radius: ${imgBorderRadius};`;
        }

        element.style.cssText = imgCss;

        if (node.prompt) element.setAttribute('data-origin-prompt', node.prompt);
        element.setAttribute(
          'data-description',
          'If the user has no requirement to update the image, you need to keep this img and its src'
        );
      }
      break;

    case TYPES.shape:
    case TYPES.path: {
      const shapeFill = node.fill || 'transparent';
      const solidShapeFill = getSolidColor(shapeFill);

      // Set background on parent TD for email compatibility
      if (solidShapeFill && solidShapeFill !== 'transparent') {
        parentTd.setAttribute('bgcolor', solidShapeFill);
        parentTd.style.backgroundColor = solidShapeFill;
      }

      if (node.stroke && node.strokeWidth) {
        parentTd.style.border = `${node.strokeWidth}px solid ${node.stroke}`;
      }

      // For modern clients, use a div for gradients and border-radius
      if (shapeFill.includes('gradient') || node.cornerRadius || solidShapeFill !== 'transparent') {
        element = document.createElement('div');
        const shapeDivStyles = {
          width: `${Math.round(w)}px`,
          height: `${Math.round(h)}px`,
          margin: '0',
          padding: '0',
          boxSizing: 'border-box',
        };

        if (shapeFill.includes('gradient')) {
          shapeDivStyles.background = shapeFill;
        } else if (solidShapeFill && solidShapeFill !== 'transparent') {
          shapeDivStyles.backgroundColor = solidShapeFill;
        }

        const shapeRadius = getBorderRadius(node);
        if (shapeRadius) {
          shapeDivStyles.borderRadius = shapeRadius;
        }

        if (node.stroke && node.strokeWidth) {
          shapeDivStyles.border = `${node.strokeWidth}px solid ${node.stroke}`;
        }

        element.style.cssText = Object.entries(shapeDivStyles)
          .map(([k, v]) => `${kebabCase(k)}: ${v}`)
          .join('; ');
        element.innerHTML = '&nbsp;'; // Prevent stripping
      } else {
        // Fallback to spacer GIF to enforce height if no div is created
        parentTd.innerHTML = `<img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" width="${Math.round(w)}" height="${Math.round(h)}" style="display:block; width:${Math.round(w)}px; height:${Math.round(h)}px;" />`;
      }
      break;
    }

    case TYPES.frame:
    case TYPES.group:
    default: {
      const children = store.api.getChildren(node.id);
      const frameFill = node.fill || 'transparent';
      const frameSolidFill = getSolidColor(frameFill);

      // Set background on parent TD for email compatibility
      if (frameSolidFill && frameSolidFill !== 'transparent') {
        parentTd.setAttribute('bgcolor', frameSolidFill);
        parentTd.style.backgroundColor = frameSolidFill;
      }

      if (node.stroke && node.strokeWidth) {
        parentTd.style.border = `${node.strokeWidth}px solid ${node.stroke}`;
      }

      if (children && children.length > 0) {
        element = document.createElement('table');
        element.setAttribute('role', 'presentation');
        element.setAttribute('border', '0');
        element.setAttribute('cellpadding', '0');
        element.setAttribute('cellspacing', '0');
        element.setAttribute('width', '100%');

        const tableStyles = {
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          margin: '0',
          padding: '0',
        };

        // Apply background to table for modern clients
        if (frameFill.includes('gradient')) {
          tableStyles.background = frameFill;
        } else if (frameSolidFill && frameSolidFill !== 'transparent') {
          tableStyles.backgroundColor = frameSolidFill;
        }

        const frameRadius = getBorderRadius(node);
        if (frameRadius) {
          tableStyles.borderRadius = frameRadius;
          tableStyles.overflow = 'hidden';
        }

        element.style.cssText = Object.entries(tableStyles)
          .map(([k, v]) => `${kebabCase(k)}: ${v}`)
          .join('; ');

        const childTbody = document.createElement('tbody');
        element.appendChild(childTbody);

        const nodeBox = store.api.getNodeBox(node);
        generateSlicedLayout(childTbody, children, store, document, nodeBox);
      } else if (frameFill && frameFill !== 'transparent') {
        // Empty frame with fill, treat as a shape.
        if (frameFill.includes('gradient') || node.cornerRadius) {
          element = document.createElement('div');
          const emptyFrameDivStyles = {
            width: `${Math.round(w)}px`,
            height: `${Math.round(h)}px`,
            margin: '0',
            padding: '0',
            boxSizing: 'border-box',
          };

          if (frameFill.includes('gradient')) {
            emptyFrameDivStyles.background = frameFill;
          } else {
            emptyFrameDivStyles.backgroundColor = frameFill;
          }

          const emptyFrameRadius = getBorderRadius(node);
          if (emptyFrameRadius) {
            emptyFrameDivStyles.borderRadius = emptyFrameRadius;
          }

          element.style.cssText = Object.entries(emptyFrameDivStyles)
            .map(([k, v]) => `${kebabCase(k)}: ${v}`)
            .join('; ');
          element.innerHTML = '&nbsp;';
        } else {
          parentTd.innerHTML = `<img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" width="${Math.round(w)}" height="${Math.round(h)}" style="display:block; width:${Math.round(w)}px; height:${Math.round(h)}px;" />`;
        }
      }
      break;
    }
  }

  if (element) {
    if (opacity < 1) {
      element.style.opacity = opacity.toString();
    }
    parentTd.appendChild(element);
  }
}

/**
 * Generates a table-based layout by slicing the design into a grid based on element boundaries.
 * This creates a highly compatible HTML structure for emails.
 * @param {HTMLTableSectionElement} tbody - The tbody element to append rows to.
 * @param {Array<object>} nodes - The list of nodes to render.
 * @param {object} store - The data store with API access.
 * @param {HTMLDocument} document - The document to create elements in.
 * @param {object} rect - The bounding box {x, y, w, h} of the layout area.
 */
function generateSlicedLayout(tbody, nodes, store, document, rect) {
  if (!nodes.length) return;

  const xCoords = new Set([0, rect.w]);
  const yCoords = new Set([0, rect.h]);
  nodes.forEach((node) => {
    const box = store.api.getNodeBox(node);
    const relX = box.x - rect.x;
    const relY = box.y - rect.y;
    xCoords.add(relX);
    xCoords.add(relX + box.w);
    yCoords.add(relY);
    yCoords.add(relY + box.h);
  });

  const sortedX = [...xCoords].filter((c) => c >= 0 && c <= rect.w + 0.001).sort((a, b) => a - b);
  const sortedY = [...yCoords].filter((c) => c >= 0 && c <= rect.h + 0.001).sort((a, b) => a - b);

  const uniqueX = sortedX.filter((x, i) => i === 0 || x - sortedX[i - 1] > 0.1);
  const uniqueY = sortedY.filter((y, i) => i === 0 || y - sortedY[i - 1] > 0.1);

  const rowCount = uniqueY.length - 1;
  const colCount = uniqueX.length - 1;
  if (rowCount <= 0 || colCount <= 0) return;

  const gridMap = Array(rowCount)
    .fill(null)
    .map(() => Array(colCount).fill(null));
  const sortedNodes = [...nodes].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      const cellMidX = uniqueX[c] + (uniqueX[c + 1] - uniqueX[c]) / 2;
      const cellMidY = uniqueY[r] + (uniqueY[r + 1] - uniqueY[r]) / 2;

      let topNode = null;
      for (const node of sortedNodes) {
        const box = store.api.getNodeBox(node);
        const relX = box.x - rect.x;
        const relY = box.y - rect.y;
        if (
          cellMidX >= relX &&
          cellMidX < relX + box.w &&
          cellMidY >= relY &&
          cellMidY < relY + box.h
        ) {
          topNode = node;
        }
      }
      gridMap[r][c] = topNode;
    }
  }

  const processed = Array(rowCount)
    .fill(null)
    .map(() => Array(colCount).fill(false));

  for (let r = 0; r < rowCount; r++) {
    const tr = document.createElement('tr');
    tr.style.cssText = 'margin: 0; padding: 0; border: 0;';

    for (let c = 0; c < colCount; c++) {
      if (processed[r][c]) continue;

      const node = gridMap[r][c];

      let colspan = 1;
      while (
        c + colspan < colCount &&
        !processed[r][c + colspan] &&
        gridMap[r][c + colspan] === node
      ) {
        colspan++;
      }

      let rowspan = 1;
      while (r + rowspan < rowCount) {
        let canSpanRow = true;
        for (let i = 0; i < colspan; i++) {
          if (processed[r + rowspan][c + i] || gridMap[r + rowspan][c + i] !== node) {
            canSpanRow = false;
            break;
          }
        }
        if (canSpanRow) rowspan++;
        else break;
      }

      const td = document.createElement('td');
      const tdWidth = uniqueX[c + colspan] - uniqueX[c];
      const tdHeight = uniqueY[r + rowspan] - uniqueY[r];

      td.setAttribute('width', Math.round(tdWidth));
      td.setAttribute('height', Math.round(tdHeight));
      if (colspan > 1) td.setAttribute('colspan', colspan);
      if (rowspan > 1) td.setAttribute('rowspan', rowspan);

      td.setAttribute('valign', 'top');
      td.style.cssText = `padding:0; margin:0; border:0; vertical-align:top; width:${Math.round(tdWidth)}px; height:${Math.round(tdHeight)}px; line-height:1px; font-size:1px; box-sizing: border-box;`;

      if (node) {
        createEmailCompatibleElement(node, td, store, document, { w: tdWidth, h: tdHeight });
      } else {
        td.innerHTML = '&nbsp;';
      }

      tr.appendChild(td);

      for (let i = 0; i < rowspan; i++) {
        for (let j = 0; j < colspan; j++) {
          processed[r + i][c + j] = true;
        }
      }
    }
    if (tr.children.length > 0) {
      tbody.appendChild(tr);
    }
  }
}

/**
 * Converts a set of nodes into a single, email-compatible HTML string.
 * @param {object} store - The data store with API access.
 * @param {Array<object>|object} nodes - The node or nodes to convert.
 * @param {object} [options] - Options for the conversion.
 * @param {boolean} [options.remove=true] - Whether to remove the temporary iframe.
 * @param {boolean} [options.useDebug=false] - Whether to render the output in a new iframe for debugging.
 * @returns {Promise<string>} The generated HTML string.
 */
export async function node2EmailHTML(store, nodes, { remove = true, useDebug = false } = {}) {
  if (!Array.isArray(nodes)) {
    nodes = [nodes];
  }
  const rect = store.api.getNodesRegion(nodes);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed; width:1px; height:1px; top:-9999px; left:-9999px;';
  document.body.appendChild(iframe);

  await new Promise((resolve) => {
    iframe.onload = resolve;
    iframe.src = 'about:blank';
  });

  const doc = iframe.contentDocument;
  doc.open();
  doc.writeln(
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">'
  );
  doc.writeln(
    '<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">'
  );
  doc.writeln('<head>');
  doc.writeln('<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />');
  doc.writeln('<meta name="viewport" content="width=device-width, initial-scale=1.0"/>');
  doc.writeln('<title>Email</title>');
  doc.writeln(
    '<!--[if gte mso 9]><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml><![endif]-->'
  );
  doc.writeln(
    '<style type="text/css">table,td{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt}img{-ms-interpolation-mode:bicubic;outline:none;text-decoration:none}a img{border:none}*{box-sizing:border-box}</style>'
  );
  doc.writeln('</head>');
  doc.writeln(`<body style="margin:0;padding:0;font-family:Arial,sans-serif;">`);
  doc.writeln('</body></html>');
  doc.close();

  const container = doc.createElement('table');
  container.setAttribute('role', 'presentation');
  container.setAttribute('border', '0');
  container.setAttribute('cellpadding', '0');
  container.setAttribute('cellspacing', '0');
  container.setAttribute('width', Math.round(rect.w));
  container.style.cssText = `width:${Math.round(rect.w)}px;max-width:${Math.round(rect.w)}px;border-collapse:collapse;table-layout:fixed;margin:0;padding:0;`;

  doc.body.appendChild(container);

  const tbody = doc.createElement('tbody');
  container.appendChild(tbody);

  generateSlicedLayout(tbody, nodes, store, doc, { x: rect.x, y: rect.y, w: rect.w, h: rect.h });

  await translateInlineImage(doc);

  const html = doc.body.innerHTML;

  if (remove) iframe.remove();

  if (useDebug) {
    const newFrame = document.createElement('iframe');
    newFrame.style.cssText = 'width:100%;height:50vh;border:1px solid #ccc;';
    document.body.appendChild(newFrame);
    const newDoc = newFrame.contentDocument || newFrame.contentWindow.document;
    newDoc.open();
    newDoc.write(`<body style="margin:0;padding:0;">${html}</body>`);
    newDoc.close();
  }

  return html;
}

export function round(num) {
  return Math.round(num);
}

export async function translateInlineImage(contentDocument) {
  const images = contentDocument.querySelectorAll('img');
  for (const img of Array.from(images)) {
    if (img.src && !img.src.startsWith('data:')) {
      try {
        const response = await fetch(img.src);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
        const blob = await response.blob();
        img.src = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn('Failed to convert image to base64:', img.src, error);
      }
    }
  }
}

export const createNodeElement = (node, tag = 'div', { useId = false } = {}) => {
  const element = document.createElement(tag);
  if (useId) {
    element.id = node.id;
    element.dataset.type = node.type;
  }
  resolveElementTransformToNode(element, node);
  return element;
};

function createStyledTextContent(node) {
  let currentIndex = 0;
  let htmlContent = '';
  const text = node.text || '';

  // Sort styles by starting position
  const sortedStyles = [...node.styles].sort((a, b) => a.selection[0] - b.selection[0]);

  for (let i = 0; i < sortedStyles.length; i++) {
    const style = sortedStyles[i];
    const [start, end] = style.selection;

    // Add text before this styled section
    if (start > currentIndex) {
      htmlContent += text.slice(currentIndex, start);
    }

    // Add styled section
    const styledText = text.slice(start, end);
    const spanStyles = [];

    if (style.fill) {
      spanStyles.push(`color: ${style.fill}`);
    }
    if (style.fontWeight) {
      spanStyles.push(`font-weight: ${style.fontWeight}`);
    }
    if (style.fontStyle) {
      spanStyles.push(`font-style: ${style.fontStyle}`);
    }
    if (style.textDecoration) {
      spanStyles.push(`text-decoration: ${style.textDecoration}`);
    }
    if (style.fontSize) {
      spanStyles.push(`font-size: ${style.fontSize}px`);
    }

    if (spanStyles.length > 0) {
      htmlContent += `<span style="${spanStyles.join('; ')}">${styledText}</span>`;
    } else {
      htmlContent += styledText;
    }

    currentIndex = end;
  }

  // Add remaining text after all styled sections
  if (currentIndex < text.length) {
    htmlContent += text.slice(currentIndex);
  }

  // Handle line breaks
  htmlContent = htmlContent.replace(/\n/g, '<br>');

  return htmlContent;
}

export function resolveElementTransformToNode(element, node) {
  if (node.transform) {
    const matrix = getNodeMatrix(node);
    // Combine rotation with matrix transform
    if (matrix && Array.isArray(matrix) && matrix.length === 6) {
      element.style.transform = `matrix(${matrix.join(',')})`;
    }
  }
}

export function createTextElementByNode(node, parent, options) {
  let element;
  const nodeBox = node;
  const positionClasses = `absolute left-[${round(nodeBox.x)}px] top-[${round(nodeBox.y)}px]`;
  const sizeClasses = `w-[${round(node.w + 1)}px] h-[${round(node.h + 1)}px]`;

  const roundedClasses = radiusToRounded(node.radius);
  const opacity = node.opacity ?? 1;
  const opacityClasses = opacity !== 1 ? `opacity-[${node.opacity}]` : '';
  const strokeClasses =
    node.borderColor && node.borderWidth
      ? `border-[${node.borderWidth}px] border-[${node.stroke}]`
      : '';

  element = createNodeElement(node, 'p', options);
  element.className = `${positionClasses} ${sizeClasses} ${strokeClasses} ${roundedClasses} ${opacityClasses}`;
  // Normalize default paragraph behavior and enable stable CJK wrapping
  element.style.margin = '0';
  element.style.boxSizing = 'border-box';
  element.style.whiteSpace = 'pre-wrap';
  element.style.wordBreak = 'normal';
  element.style.overflowWrap = 'break-word';
  element.style.lineBreak = 'strict';
  element.style.textRendering = 'geometricPrecision';
  // Handle text color and gradients
  if (node.fill?.includes('gradient')) {
    element.style.color = 'transparent';
    element.style.backgroundImage = node.fill;
    element.style.backgroundClip = 'text';
    element.style.webkitBackgroundClip = 'text';
    element.style.backgroundSize = '100% 100%';
    element.style.backgroundRepeat = 'no-repeat';
    element.style.webkitTextFillColor = 'transparent';
  } else {
    element.style.color = node.fill || '';
  }

  // Handle text shadow
  if (node.shadow && node.shadow !== 'none') {
    const shadowValue = node.shadow || '';
    element.style.textShadow = shadowValue;
    element.setAttribute('data-shadow', shadowValue);
  }

  if (node.fillMode) {
    const fillMode = node.fillMode || '';
    element.setAttribute('data-fill-mode', fillMode);
  }

  // Handle padding
  const padding = getPaddingBox(node);
  if (padding.x || padding.y) {
    const paddingString = serializePadding(padding);
    element.style.padding = paddingString;
  }

  // Basic typography properties
  if (node.fontFamily) {
    element.style.fontFamily = node.fontFamily || '';
  }

  element.style.fontSize = `${node.fontSize}px`;

  if (node.fontWeight && node.fontWeight !== 'normal' && node.fontWeight !== '400') {
    element.style.fontWeight = node.fontWeight;
  }

  if (node.fontStyle && node.fontStyle !== 'normal') {
    element.style.fontStyle = node.fontStyle;
  }

  if (node.lineHeight) {
    element.style.lineHeight =
      typeof node.lineHeight === 'number'
        ? Number(node.lineHeight.toFixed(2))
        : node.lineHeight || '';
  }

  if (node.textAlign) {
    element.style.textAlign = node.textAlign || '';
  }

  if (node.background) {
    element.style.background = node.background || 'transparent';
  }

  // Handle letter spacing
  if (node.letterSpacing) {
    element.style.letterSpacing = `${node.letterSpacing}px`;
  }

  // Handle text decoration
  if (node.textDecoration || node.decoration) {
    const decoration = node.textDecoration || node.decoration;
    if (decoration === 'underline') {
      element.style.textDecoration = 'underline';
    } else if (decoration === 'lineThrough' || decoration === 'line-through') {
      element.style.textDecoration = 'line-through';
    }
  }

  // Handle text stroke
  const stroke = resolveTextStrokeProps(node);
  if (stroke) {
    element.style.webkitTextStroke = `${stroke.width}px ${stroke.stroke}`;
    element.style.paintOrder = 'stroke fill';
  }

  // Handle multiple strokes (use data attribute for complex strokes)
  const strokes = resolveTextStrokes(node);
  if (strokes && strokes.length > 0) {
    element.setAttribute('data-strokes', strokes);
  }

  // Handle text highlights (use data attribute for complex highlights)
  if (node.highlights && node.highlights.length > 0) {
    element.setAttribute('data-highlights', JSON.stringify(node.highlights));
  }

  // Handle text effects (use data attribute for complex effects)
  if (node.effect) {
    element.setAttribute('data-effect', JSON.stringify(node.effect));
  }

  // Handle vertical text alignment
  if (node.alignItems) {
    element.style.display = 'flex';
    element.style.flexDirection = 'row';

    if (node.alignItems === 'center') {
      element.style.alignItems = 'center';
    } else if (node.alignItems === 'bottom') {
      element.style.alignItems = 'flex-end';
    } else if (node.alignItems === 'flex-start' || node.alignItems === 'top') {
      element.style.alignItems = 'flex-start';
    }

    if (node.textAlign === 'center') {
      element.style.justifyContent = 'center';
    } else if (node.textAlign === 'right') {
      element.style.justifyContent = 'flex-end';
    } else {
      element.style.justifyContent = 'flex-start';
    }
  }

  // Handle styled text with nested elements
  if (node.styles && node.styles.length > 0) {
    element.setAttribute('data-styles', JSON.stringify(node.styles));
    element.innerHTML = createStyledTextContent(node);
  } else {
    // Simple text content
    element.textContent = node.text || '';
  }

  // Store text layout properties as data attributes for complex rendering
  if (node.minW || node.minH || node.maxW || node.maxH) {
    const constraints = {};
    if (node.minW) constraints.minW = node.minW;
    if (node.minH) constraints.minH = node.minH;
    if (node.maxW) constraints.maxW = node.maxW;
    if (node.maxH) constraints.maxH = node.maxH;
    element.setAttribute('data-constraints', JSON.stringify(constraints));
  }

  // Store text size type for layout
  if (node.width || node.height) {
    const sizeType = {};
    if (node.width) sizeType.width = node.width;
    if (node.height) sizeType.height = node.height;
    element.setAttribute('data-size-type', JSON.stringify(sizeType));
  }

  if (parent) {
    parent.appendChild(element);
  }

  return element;
}

// radius: null
// radius: 10
// radius: 10px
// radius: 10px 5px
// radius: 10px 10px 1px 2px
export function radiusToRounded(radius) {
  if (!radius || radius === '0px' || radius === 0) return '';

  // Handle different radius formats
  if (typeof radius === 'string') {
    const trimmedRadius = radius.trim();

    // Handle multiple values (e.g., "10px 5px", "10px 10px 1px 2px")
    const values = trimmedRadius.split(/\s+/);

    if (values.length === 1) {
      // Single value: "10px" or "10"
      const numericValue = values[0].replace('px', '');
      if (!isNaN(numericValue) && numericValue !== '0') {
        return `rounded-[${numericValue}px]`;
      }
    } else if (values.length === 2) {
      // Two values: "10px 5px" -> top-left/bottom-right and top-right/bottom-left
      const topLeft = values[0].replace('px', '');
      const topRight = values[1].replace('px', '');
      if (!isNaN(topLeft) && !isNaN(topRight)) {
        if (topLeft === topRight) {
          return `rounded-[${topLeft}px]`;
        } else {
          return `rounded-tl-[${topLeft}px] rounded-tr-[${topRight}px] rounded-bl-[${topLeft}px] rounded-br-[${topRight}px]`;
        }
      }
    } else if (values.length === 4) {
      // Four values: "10px 10px 1px 2px" -> top-left, top-right, bottom-right, bottom-left
      const topLeft = values[0].replace('px', '');
      const topRight = values[1].replace('px', '');
      const bottomRight = values[2].replace('px', '');
      const bottomLeft = values[3].replace('px', '');

      if (!isNaN(topLeft) && !isNaN(topRight) && !isNaN(bottomRight) && !isNaN(bottomLeft)) {
        // Check if all values are the same
        if (topLeft === topRight && topRight === bottomRight && bottomRight === bottomLeft) {
          return `rounded-[${topLeft}px]`;
        } else {
          return `rounded-tl-[${topLeft}px] rounded-tr-[${topRight}px] rounded-br-[${bottomRight}px] rounded-bl-[${bottomLeft}px]`;
        }
      }
    }
  }

  // Handle numeric values
  if (typeof radius === 'number' && radius > 0) {
    return `rounded-[${radius}px]`;
  }

  return '';
}

export function getStyleNumber(style, key) {
  if (isValidNumber(style[key])) {
    return safeParse(style[key]);
  }

  return null;
}

export function getInlineData(element, key) {
  const inlineData = element.getAttribute(`data-${key}`);
  let data = null;

  if (inlineData) {
    try {
      data = JSON.parse(inlineData);
      if (!Array.isArray(data)) {
        console.warn('Invalid highlight data: expected array');
        return null;
      }
      return data;
    } catch (e) {
      console.error(`Error parsing data-${key}:`, e);
      return null;
    }
  }
}

export function getInlineDataValue(element, key, { type = 'string' } = {}) {
  const inlineData = element.getAttribute(`data-${key}`);
  let data = null;

  if (inlineData) {
    try {
      if (type === 'string') {
        return inlineData;
      } else if (type === 'number') {
        return parseFloat(inlineData);
      } else if (type === 'object') {
        data = JSON.parse(inlineData);
        return data;
      } else if (type === 'array') {
        data = JSON.parse(inlineData);
        if (!Array.isArray(data)) {
          console.warn('Invalid highlight data: expected array');
          return null;
        }
        return data;
      }
      return null;
    } catch (e) {
      console.error(`Error parsing data-${key}:`, e);
      return null;
    }
  }
}
